import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BarChartConfig } from '@ddt-web/chart';
import { ToggleType } from '@ddt-web/common';
import { ColorsConfig, ScenarioTesterDCR } from '@ddt-web/config';
import { CellData, ComponentData, TabData, TableData, TabUtil } from '@ddt-web/data-service';
import { MultiViewComponentType } from '@ddt-web/generic-utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import { Subject } from 'rxjs';

import { SimpleScenariosTableComponent } from '../simple-scenarios-table/simple-scenarios-table.component';

@UntilDestroy()
@Component({
	selector: 'sc-scenarios-chart-table',
	templateUrl: './scenarios-chart-table.component.html',
	styleUrls: ['./scenarios-chart-table.component.scss']
})
export class ScenariosChartTableComponent extends SimpleScenariosTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

	public readonly toggleType = ToggleType;
	public readonly multiViewComponentType = MultiViewComponentType;

	@Input() public data: ComponentData;
	@Input() public dataTabId: string;
	@Input() public dcrName: string;
	@Input() public storeName: string;
	@Input() public defaultScenariosValue: string = ScenarioTesterDCR.DEFAULT_SCENARIOS_VALUE;
	@Input() public defaultScenariosKey: string = ScenarioTesterDCR.DEFAULT_SCENARIOS_KEY;
	@Input() public displayISOCurrency: string;
	@ViewChild('barChartContainer', { static: false }) public container: ElementRef;

	public componentState: ToggleType = ToggleType.Chart;
	public componentDCR: Object;
	public componentTitle: string;
	public componentSource: string;
	public asOfDate: string;
	public selectedScenarios: Array<CellData>;
	public allScenarios: Array<CellData>;
	public selectedScenarioKeys: Array<string> = [];
	public defaultScenarioKeys: Array<string> = [];
	public scenariosTableData: TableData;
	public scenariosTabData: TabData;
	public scenariosTabs: TabData[];
	public scenarioNameColumnClass: string;
	public scenarioValueColumnClass: string;
	public scenarioChartColumnClass: string;
	public modalScenarioData: any;
	public colors = ColorsConfig.PORTFOLIO_COLORS;
	public iconType = 'box';
	public categories = ['-50.0', '-25.0', '0.0', '25.0', '50.0'];
	public refreshEvent$: Subject<boolean> = new Subject();
	public observer: ResizeObserver;

	public customAttributes = {
		legend: {
			enabled: false
		},
		chart: {
			height: 85,
			marginTop: 0,
			marginBottom: 0,
			marginLeft: 0,
			marginRight: 0
		},
		tooltip: {
			formatter: function (): string {
				return `Performance: ${(this.y * 100).toFixed(2)} %`;
			}
		},
		xAxis: {
			visible: true,
			opposite: true,
			categories: ['-50.0', '-25.0', '0.0', '25.0', '50.0']
		},
		yAxis: {
			tickPositions: [-0.5, -0.25, 0, 0.25, 0.5],
			gridLineWidth: 1,
			plotLines: [
				{
					color: '#a5a5a5',
					width: 1,
					value: 0,
					dashStyle: 'solid',
					zIndex: 9
				}
			]
		}
	};

	public ngOnInit(): void {
		// send a refreshEvent if the chart element resizes
		this.observer = new ResizeObserver(entries => {
			this.refreshEvent$.next(true);
		});
	}

	public ngOnChanges(): void {
		if (this.data.success && this.data.portfolioData) {
			this.componentDCR = ScenarioTesterDCR[this.dcrName];
			this.componentTitle = this.contentService.get(this.componentDCR['TITLE_KEY'], this.componentDCR['TITLE_VALUE']);
			this.componentSource = this.contentService.get(this.componentDCR['SOURCE_KEY'], this.componentDCR['SOURCE_VALUE']);
			this.defaultScenarioKeys = this.contentService.get(this.defaultScenariosKey, this.defaultScenariosValue).split(',');
			this.scenariosTabData = this.data.getTabById(this.dataTabId);
			this.scenariosTableData = get(this.scenariosTabData, 'tableData[0]');
			const asOfDateCellData = get(this.data, 'portfolioData[0]').find(column => column.config.id === 'asOfDate');
			this.asOfDate = get(asOfDateCellData, 'formattedValue');
			if (this.scenariosTableData) {
				BarChartConfig.setTickPositionsForChart(this.scenariosTabData, this.customAttributes);
				this.categories = get(this.customAttributes, 'xAxis.categories', []);
				this.scenariosTabs = TabUtil.extractMultipleTabsFromTabData(this.scenariosTabData);
				const { scenarioNameColumnClass, scenarioValueColumnClass, scenarioChartColumnClass } = this.scenarioService.assignColumnClasses(this.scenariosTableData.headersX);
				this.scenarioNameColumnClass = scenarioNameColumnClass;
				this.scenarioValueColumnClass = scenarioValueColumnClass;
				this.scenarioChartColumnClass = scenarioChartColumnClass;
				this.allScenarios = this.scenariosTableData.headersY;
			}
			this.updateSelectedScenarios();
		}
	}

	public ngAfterViewInit(): void {
		// observe chart container to trigger chart refreshes
		this.observer.observe(this.container.nativeElement);
	}

	public toggleChange(event: ToggleType): void {
		this.componentState = event;
	}

	public ngOnDestroy(): void {
		this.observer.disconnect();
	}
}
